import React from "react";
import {
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Link } from "react-router-dom";
import { getFileTypeIcon } from "../../../../icons/FileTypeIcons";
import { getUrlForStaticApi } from "../../../../utils/urlStaticFiles";
import downloadFile from "../../../../utils/downloadFile";
import CloudDownloadIcon from "../../../../icons/CloudDownloadIcon";
import moment from "moment";
import formatFileSize from "../../../../utils/formatFileSize";
import {
  getLinkedEntityDisplayItems,
  getLinkedEntityPath,
  getLinkedEntitiesColumnTitle,
  groupLinkedEntitiesBySection,
  hasMixedLinkedEntitySections,
} from "../../helpers";

const EntityName = ({ entity, classes }) => {
  const href = getLinkedEntityPath(entity);
  if (href) {
    return (
      <Link to={href} target="_blank" rel="noopener noreferrer" className={classes.entityLink}>
        {entity.name}
      </Link>
    );
  }
  return <span className={classes.entityText}>{entity.name}</span>;
};

const EntityListInline = ({ items, classes }) => {
  const fullText = items.map((entity) => entity.name).join(", ");
  return (
    <Tooltip arrow title={fullText}>
      <div className={classes.entityListInline}>
        {items.map((entity, i) => (
          <React.Fragment key={`${entity.section}:${entity.id || entity.name}:${i}`}>
            {i > 0 && <span className={classes.entitySeparator}>, </span>}
            <EntityName entity={entity} classes={classes} />
          </React.Fragment>
        ))}
      </div>
    </Tooltip>
  );
};

const isHtmlDocument = (doc) => {
  const format = String(doc.format || "").toLowerCase().trim();
  if (format === "html" || format === "htm") return true;
  const name = String(doc.original_name || "").toLowerCase();
  return name.endsWith(".html") || name.endsWith(".htm");
};

const FileName = ({ doc, classes }) => {
  const isHtml = isHtmlDocument(doc) && doc.url;

  const handleOpen = (e) => {
    e.preventDefault();
    if (!doc.url) return;
    window.open(`/documents/view?url=${encodeURIComponent(doc.url)}`, "_blank");
  };

  const content = isHtml ? (
    <span
      role="link"
      tabIndex={0}
      className={classes.fileNameLink}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleOpen(e);
      }}
    >
      {doc.original_name}
    </span>
  ) : (
    <span className={classes.fileName}>{doc.original_name}</span>
  );

  return (
    <Tooltip arrow title={isHtml ? "Открыть в новой вкладке" : doc.original_name}>
      <span className={classes.fileNameWrap}>{content}</span>
    </Tooltip>
  );
};

const LinkedEntitiesCell = ({ doc, classes }) => {
  const entities = getLinkedEntityDisplayItems(doc);
  if (entities.length === 0) return "—";

  if (!hasMixedLinkedEntitySections(doc)) {
    return <EntityListInline items={entities} classes={classes} />;
  }

  const groups = groupLinkedEntitiesBySection(doc);
  return (
    <div className={classes.entityGroups}>
      {groups.map((group) => (
        <div key={group.section} className={classes.entityGroup}>
          <span className={classes.entityGroupLabel}>{group.label}</span>
          <EntityListInline items={group.items} classes={classes} />
        </div>
      ))}
    </div>
  );
};

const DocumentsTable = ({ documents, sectionKey, highlightDocumentId }) => {
  const classes = useStyles();
  const entitiesColumnTitle = React.useMemo(() => {
    const allItems = documents.flatMap((doc) => getLinkedEntityDisplayItems(doc));
    return getLinkedEntitiesColumnTitle(allItems);
  }, [documents]);

  React.useEffect(() => {
    if (!highlightDocumentId) return undefined;

    const targetId = String(highlightDocumentId);
    const scrollToRow = () => {
      const row = Array.from(window.document.querySelectorAll("[data-document-id]")).find(
        (el) => el.getAttribute("data-document-id") === targetId
      );
      if (!row) return false;
      // Пока Collapse закрыт, высота строки 0 — ждём раскрытия
      if (row.getBoundingClientRect().height < 1) return false;
      row.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      return true;
    };

    // Accordion Collapse ~300ms — ждём раскрытие секции/типа, затем скроллим
    let attempts = 0;
    const maxAttempts = 12;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (scrollToRow() || attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 100);

    return () => window.clearInterval(timer);
  }, [highlightDocumentId, documents]);

  return (
    <Table size="small" className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell className={classes.thName}>Название</TableCell>
          <TableCell>{entitiesColumnTitle}</TableCell>
          <TableCell className={classes.thDate}>Дата загрузки</TableCell>
          <TableCell className={classes.thAction} />
        </TableRow>
      </TableHead>
      <TableBody>
        {documents.map((doc) => {
          const FileIcon = getFileTypeIcon(doc.format);
          const isHighlighted = !!highlightDocumentId && String(doc.id) === String(highlightDocumentId);
          return (
            <TableRow
              key={doc.id}
              hover
              data-document-id={doc.id}
              className={isHighlighted ? classes.highlightRow : undefined}
            >
              <TableCell>
                <div className={classes.nameCell}>
                  <FileIcon className={classes.fileIcon} />
                  <div className={classes.nameInfo}>
                    <FileName doc={doc} classes={classes} />
                    {doc.size > 0 && <span className={classes.fileSize}>{formatFileSize(doc.size)}</span>}
                  </div>
                </div>
              </TableCell>
              <TableCell className={classes.entitiesCell}>
                <LinkedEntitiesCell doc={doc} classes={classes} />
              </TableCell>
              <TableCell className={classes.dateCell}>{doc.created ? moment(doc.created).format("DD.MM.YYYY") : "—"}</TableCell>
              <TableCell>
                <Tooltip arrow title="Скачать">
                  <span>
                    <IconButton
                      size="small"
                      className={classes.downloadBtn}
                      disabled={!doc.url}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (doc.url) downloadFile(getUrlForStaticApi(doc.url), doc.original_name);
                      }}
                    >
                      <CloudDownloadIcon color="#A1A1AA" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const useStyles = makeStyles(() => ({
  table: {
    "& .MuiTableCell-root": { borderColor: "#E4E4E7" },
  },
  highlightRow: {
    backgroundColor: "#E0E7FF !important",
    transition: "background-color 0.2s ease",
    "& > .MuiTableCell-root": {
      backgroundColor: "#E0E7FF !important",
    },
    "& > .MuiTableCell-root:first-of-type": {
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        backgroundColor: "#432DD7",
        pointerEvents: "none",
        zIndex: 1,
      },
    },
    "&:hover": {
      backgroundColor: "#E0E7FF !important",
      "& > .MuiTableCell-root": {
        backgroundColor: "#E0E7FF !important",
      },
    },
  },
  thName: { minWidth: 200 },
  thDate: { whiteSpace: "nowrap" },
  thAction: { width: 48 },
  nameCell: { display: "flex", alignItems: "flex-start", gap: 8 },
  fileIcon: { fontSize: "20px !important", flexShrink: 0, marginTop: 2 },
  nameInfo: { minWidth: 0, display: "flex", flexDirection: "column" },
  fileNameWrap: { display: "block", minWidth: 0, overflow: "hidden" },
  fileName: { fontSize: 14, fontWeight: 500, color: "#27272A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" },
  fileNameLink: {
    fontSize: 14,
    fontWeight: 500,
    color: "#27272A",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": { color: "#4F39F6", textDecoration: "underline" },
  },
  fileSize: { fontSize: 12, color: "#71717A", lineHeight: "16px" },
  entitiesCell: { verticalAlign: "top", maxWidth: 0, width: "40%" },
  entityGroups: { display: "flex", flexDirection: "column", gap: 10 },
  entityGroup: { display: "flex", flexDirection: "column", gap: 4 },
  entityGroupLabel: { fontSize: 11, fontWeight: 600, lineHeight: "16px", color: "#71717A", textTransform: "uppercase", letterSpacing: "0.02em" },
  entityListInline: {
    display: "block",
    lineHeight: "20px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  entitySeparator: { fontSize: 14, color: "#27272A" },
  entityLink: {
    display: "inline",
    fontSize: 14,
    color: "#4F39F6",
    cursor: "pointer",
    textDecoration: "none",
    lineHeight: "20px",
    "&:hover": { textDecoration: "underline" },
  },
  entityText: { display: "inline", fontSize: 14, color: "#27272A", lineHeight: "20px" },
  dateCell: { fontSize: 14, whiteSpace: "nowrap" },
  downloadBtn: { color: "#A1A1AA", padding: 4 },
}));

export default DocumentsTable;
